
import React, { createContext, useContext, useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { TipStep } from "@/components/ui/info-tip"

interface TourContextType {
  startTour: (tourId: string) => void
  endTour: (tourId: string) => void
  currentTour: string | null
  getCurrentTourSteps: () => TipStep[]
  hasSeenTour: (tourId: string) => boolean
  isToursDisabled: () => boolean
  setToursDisabled: (disabled: boolean) => void
}

const TourContext = createContext<TourContextType | undefined>(undefined)

export const tours: Record<string, TipStep[]> = {
  dashboard: [
    {
      target: ".stats-section",
      title: "Dashboard Overview",
      content: "Get a quick overview of your key metrics and current progress.",
      position: "bottom"
    },
    {
      target: "#authority-signals",
      title: "Authority Signals",
      content: "Track recent activities and important updates related to your marketing efforts.",
      position: "right"
    },
    {
      target: "#daily-insights",
      title: "Daily Insights",
      content: "Review your daily performance metrics and AI-generated alerts.",
      position: "left"
    }
  ],
  analytics: [
    {
      target: "#analytics-filter-section",
      title: "Customize Your Analytics View",
      content: "Filter your data by date range, sources, and other parameters to get the insights you need.",
      position: "bottom"
    },
    {
      target: "#analytics-tabs",
      title: "Multiple Analytics Perspectives",
      content: "Navigate between different views of your data to get a complete picture of your performance.",
      position: "bottom"
    },
    {
      target: "#analytics-metrics-overview",
      title: "Key Performance Metrics",
      content: "Get an at-a-glance view of your most important performance indicators.",
      position: "bottom"
    },
    {
      target: "#analytics-review-trends",
      title: "Visualize Your Growth",
      content: "Track your performance over time with interactive charts and graphs.",
      position: "top"
    },
    {
      target: "#analytics-platform-distribution",
      title: "Platform Breakdown",
      content: "See how your performance varies across different platforms and channels.",
      position: "left"
    }
  ],
  projectManagement: [
    {
      target: "#project-management-cards",
      title: "Project Management Suite",
      content: "Access all your project management tools from this central dashboard.",
      position: "top"
    },
    {
      target: "a[href='/project-management/projects']",
      title: "Project Boards",
      content: "Organize and track your projects with customizable kanban boards.",
      position: "right"
    },
    {
      target: "a[href='/project-management/tasks']",
      title: "Task Management",
      content: "Create, assign, and track tasks across all your projects.",
      position: "right"
    },
    {
      target: "a[href='/project-management/documentation']",
      title: "Project Documentation",
      content: "Store and organize all your project-related documents in one place.",
      position: "left"
    },
    {
      target: "a[href='/project-management/team-management']",
      title: "Team Collaboration",
      content: "Manage your team members, roles, and permissions.",
      position: "left"
    }
  ]
}

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentTour, setCurrentTour] = useState<string | null>(null)
  const [toursDisabled, setToursDisabled] = useState<boolean>(false)
  const [seenTours, setSeenTours] = useState<string[]>(() => {
    const saved = localStorage.getItem("seenTours")
    return saved ? JSON.parse(saved) : []
  })
  const location = useLocation()

  useEffect(() => {
    // Save seen tours to localStorage
    localStorage.setItem("seenTours", JSON.stringify(seenTours))
  }, [seenTours])

  useEffect(() => {
    // Auto-start tour based on route if user hasn't seen it before
    // and tours are not disabled
    if (toursDisabled) return

    const path = location.pathname.split("/")[1] || "home"
    
    if (tours[path] && !seenTours.includes(path)) {
      // Small delay to ensure the page has loaded
      const timer = setTimeout(() => {
        startTour(path)
      }, 1000)
      
      return () => clearTimeout(timer)
    }
  }, [location.pathname, toursDisabled])

  const startTour = (tourId: string) => {
    if (toursDisabled) return
    setCurrentTour(tourId)
  }

  const endTour = (tourId: string) => {
    setCurrentTour(null)
    
    // Mark tour as seen
    if (!seenTours.includes(tourId)) {
      setSeenTours([...seenTours, tourId])
    }
  }

  const hasSeenTour = (tourId: string) => {
    return seenTours.includes(tourId)
  }

  const getCurrentTourSteps = () => {
    return currentTour ? tours[currentTour] || [] : []
  }

  const isToursDisabled = () => {
    return toursDisabled
  }

  return (
    <TourContext.Provider value={{ 
      startTour, 
      endTour, 
      currentTour, 
      getCurrentTourSteps,
      hasSeenTour,
      isToursDisabled,
      setToursDisabled
    }}>
      {children}
    </TourContext.Provider>
  )
}

export const useTour = () => {
  const context = useContext(TourContext)
  if (!context) {
    throw new Error("useTour must be used within a TourProvider")
  }
  return context
}
